// Theme switch — dark is the default; light is the website palette
// (see docs/brand-tokens.css [data-theme="light"]). "System" follows the
// OS preference live (08-settings mockup).

const KEY = 'ss-os-theme';

export type Theme = 'dark' | 'light' | 'system';

export function nextTheme(current: Theme, systemPrefersLight: boolean): 'dark' | 'light' {
  if (current === 'dark') return 'light';
  if (current === 'light') return 'dark';
  return systemPrefersLight ? 'dark' : 'light';
}

let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;
// One shared MediaQueryList: matchMedia() returns a NEW object per call, so
// removeEventListener on a fresh instance never detaches the old listener
// (stale system listeners kept flipping the theme after an explicit choice).
let mq: MediaQueryList | null = null;
const media = () => (mq ??= window.matchMedia('(prefers-color-scheme: light)'));

function render(light: boolean): void {
  if (light) document.documentElement.dataset.theme = 'light';
  else delete document.documentElement.dataset.theme;
  // Same grammar as ss:mutated — lets the sidebar badge and the Settings
  // Seg stay in agreement no matter which control changed the theme.
  window.dispatchEvent(new Event('ss:theme'));
}

export function currentTheme(): Theme {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch {
    // storage blocked (private mode / SecurityError) — fall back to dark
  }
  return stored === 'light' || stored === 'system' ? stored : 'dark';
}

export function applyTheme(theme: Theme): void {
  // Persist BEFORE render: render() emits ss:theme, and listeners re-read
  // currentTheme() — writing after would hand them the stale preference.
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // theme still applies for this page-load; it just won't persist
  }
  if (mediaListener) {
    media().removeEventListener('change', mediaListener);
    mediaListener = null;
  }
  if (theme === 'system') {
    render(media().matches);
    mediaListener = (e) => render(e.matches);
    media().addEventListener('change', mediaListener);
  } else {
    render(theme === 'light');
  }
}

export function initTheme(): void {
  applyTheme(currentTheme());
}
